import React from "react";
import "./App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "@/lib/theme";
import { ToastProvider } from "@/lib/toast";
import { LoadingProvider } from "@/lib/loading";
import { AuthProvider, useAuth } from "@/lib/auth";
import { PendingApprovalsProvider } from "@/lib/pending";
import { PresenceTracker } from "@/lib/presence";
import { InstallPrompt } from "@/components/app/InstallPrompt";
import { useBrowserNotifications } from "@/lib/desktopNotify";
import { TabsLayout } from "@/components/app/Layout";
import { Spinner } from "@/components/app/Spinner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Verify from "@/pages/Verify";
import { ForgotEmail, ForgotPassword } from "@/pages/Forgot";
import Dashboard from "@/pages/Dashboard";
import Tasks from "@/pages/Tasks";
import Todos from "@/pages/Todos";
import AI from "@/pages/AI";
import Profile from "@/pages/Profile";
import ProfileEdit from "@/pages/ProfileEdit";
import Workspaces from "@/pages/Workspaces";
import Notifications from "@/pages/Notifications";
import Team from "@/pages/Team";
import Reports from "@/pages/Reports";
import TaskNew from "@/pages/TaskNew";
import TaskDetail from "@/pages/TaskDetail";
import Security from "@/pages/Security";
import Admin from "@/pages/Admin";
import { DeleteAccount, PrivacyPolicy } from "@/pages/Legal";

const AUTH_ROUTES = new Set(["login", "register"]);
const PUBLIC_ROUTES = new Set(["forgot-password", "forgot-email", "verify-email", "privacy-policy", "delete-account"]);
const TAB_ROUTES = new Set(["", "tasks", "todos", "ai", "profile"]);

/** Mirrors the mobile RootNavigation guard (auth → verify → workspace → tabs). */
function useRedirect() {
  const { user, loading, activeWorkspace } = useAuth();
  const location = useLocation();
  if (loading) return null;
  const first = location.pathname.split("/")[1] || "";
  const inAuth = AUTH_ROUTES.has(first);
  const isPublic = PUBLIC_ROUTES.has(first);
  const onVerify = first === "verify";
  if (!user) return !inAuth && !isPublic ? "/login" : null;
  const needsEmail = !user.email_verified;
  const needsMobile = !user.mobile_verification_exempt && (!user.mobile_verified || !user.mobile_number);
  const needsVerify = needsEmail || needsMobile;
  if (needsVerify && !onVerify) return "/verify";
  if (!needsVerify && onVerify) {
    if (new URLSearchParams(location.search).get("target") === "mobile") return "/profile/edit";
    return activeWorkspace ? "/" : "/workspaces";
  }
  if (inAuth) return activeWorkspace ? "/" : "/workspaces";
  if (!activeWorkspace && TAB_ROUTES.has(first)) return "/workspaces";
  return null;
}

function Splash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-a-bg">
      <span className="block h-[52px] w-16 overflow-hidden"><img src="/branding/assignoo-tick.png" alt="Assignoo" className="w-16 max-w-none" /></span>
      <Spinner size={32} />
    </div>
  );
}

const tab = (el) => <TabsLayout>{el}</TabsLayout>;

function AppRoutes() {
  const { loading, user } = useAuth();
  const target = useRedirect();
  const location = useLocation();
  useBrowserNotifications({ userId: user?.id });
  if (loading) return <Splash />;
  if (target && target !== location.pathname) return <Navigate to={target} replace />;
  return (
    <>
      <PresenceTracker userId={user?.id} />
      {user ? <InstallPrompt /> : null}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify" element={<Verify />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/forgot-email" element={<ForgotEmail />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        <Route path="/" element={tab(<Dashboard />)} />
        <Route path="/tasks" element={tab(<Tasks />)} />
        <Route path="/todos" element={tab(<Todos />)} />
        <Route path="/ai" element={tab(<AI />)} />
        <Route path="/profile" element={tab(<Profile />)} />
        <Route path="/profile/edit" element={<ProfileEdit />} />
        <Route path="/workspaces" element={<Workspaces />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/team" element={<Team />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/task/new" element={<TaskNew />} />
        <Route path="/task/:id" element={<TaskDetail />} />
        <Route path="/security" element={<Security />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <LoadingProvider>
          <BrowserRouter>
            <AuthProvider>
              <PendingApprovalsProvider>
                <AppRoutes />
              </PendingApprovalsProvider>
            </AuthProvider>
          </BrowserRouter>
        </LoadingProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
